import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Header from '../layout/Header.jsx';
import { useAsyncResource } from '../../../commons/hooks/useAsyncResource';
import ListResource from '../../../admin/components/lists/ListResource.jsx';
import useSearchParams from '~/commons/hooks/useSearchParams';
import { env } from '~/commons/config';

const qualityColor = {
  Perfect: '#4EBF35',
  Imperfect: '#F9A000',
  Defective: '#ED0006'
};

const PageStocks = () => {
  const history = useHistory();
  const roles = _.get(env, 'user.roles');

  const { resource: { data: advFilterData = {} } = {} } = useAsyncResource(async () => {
    if (_.isEqual(roles, ['customer'])) {
      return axios.get('/api/core/stocks/advFilterData?shared=true');
    }
    return axios.get('/api/core/stocks/advFilterData');
  }, []);

  const { resource: { data: showrooms } = {} } = useAsyncResource(async () => {
    if (_.isEqual(roles, ['customer'])) {
      return { data: [] };
    }
    return axios.get('/api/core/users/self/showrooms');
  }, []);

  const [searchParams, setSearchParams] = useSearchParams({});
  const filter = {
    brand: searchParams.get('brand') || '',
    construction: searchParams.get('construction') || '',
    collection: searchParams.get('collection') || '',
    status: searchParams.get('status') || '',
    orgId: searchParams.get('orgId') || '',
    location: searchParams.get('location') || '',
  };

  const showrms = _.uniqBy(showrooms, 'orgId');

  return (
    <div className="py-4">
      <Header>Stocks</Header>
      <ListResource
        api='/api/core/stocks'
        actions={{
          create: false,
          remove: false,
        }}
        filter={{
          ..._.pickBy(filter, (v) => v),
          ..._.isEqual(roles, ['customer']) ? {
            shared: true,
            status: 'Active',
           } : {},
        }}
        searchable
        sort={{ itemCode: 1 }}
        onRowClick={(row) => {
          history.push(`/portal/stocks/${row._id}`);
        }}
        cols={_.compact([
          !_.isEqual(roles, ['customer']) && {
            key: 'quality',
            sortable: true,
            children: ' ',
            getter: (v) => {
              return v ? (
                <div style={{
                  width: '14px',
                  height: '14px',
                  backgroundColor: qualityColor[v],
                  borderRadius: '50%',
                }} />
              ) : null;
            }
          },
          {
            key: 'photos',
            getter: (v) => (v?.[0] ? (
              <img
                src={v[0]}
                alt='s'
                style={{
                  maxWidth: '120px',
                  maxHeight: '120px',
                }}
              />
            ) : ''),
          },
          {
            key: 'prices.currency',
            sortable: true,
            children: 'Currency',
          },
          {
            key: 'prices.retailPrice',
            sortable: true,
            children: 'Price',
            getter: (v) => (Number(v) || 0).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          },
          {
            key: 'itemCode',
            sortable: true,
            children: 'Item Code',
          },
          {
            key: 'serialNo',
            sortable: true,
            children: 'Serial Number',
          },
          {
            key: 'category',
            sortable: true,
            children: 'Category',
          },
          { key: 'size', sortable: true },
          {
            key: 'design.design',
            sortable: true,
            children: 'Design',
          },
          { key: 'location', sortable: true },
          {
            key: 'yarnContent',
            sortable: true,
            children: 'Color/Content',
            getter: (v) => _.join(v, ', '),
          },
          {
            key: 'status',
            sortable: true,
          },
          { key: 'remarks', sortable: true, getter: (v) => _.truncate(v, { length: 120 }) },
        ])}
        listHeader={(
          <div className="form-row mb-3">
            <div className="col">
              <select
                className="custom-select"
                value={filter.brand}
                onChange={(e) => {
                  setSearchParams({ brand: e.target.value });
                }}
              >
                <option value="">All Brand</option>
                {_.map(_.orderBy(advFilterData?.brand, '_id'), ({ _id: v }) => (
                  <option value={v} key={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="col">
              <select
                className="custom-select"
                value={filter.construction}
                onChange={(e) => {
                  setSearchParams({ construction: e.target.value });
                }}
              >
                <option value="">All Construction</option>
                {_.map(_.orderBy(advFilterData?.construction, '_id'), ({ _id: v }) => (
                  <option value={v} key={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="col">
              <select
                className="custom-select"
                value={filter.collection}
                onChange={(e) => {
                  setSearchParams({ collection: e.target.value });
                }}
              >
                <option value="">All Collection</option>
                {_.map(_.orderBy(advFilterData?.collection, ({ _id: v, name }) => name || v), ({ _id: v, name }) => (
                  <option value={v} key={v}>{name || v}</option>
                ))}
              </select>
            </div>
            {!_.isEqual(roles, ['customer']) && <div className="col">
              <select
                className="custom-select"
                value={filter.status}
                onChange={(e) => {
                  setSearchParams({ status: e.target.value });
                }}
              >
                <option value="">All Status</option>
                {(env?.status?.stocks || []).map((v) => (
                  <option value={v} key={v}>
                    {_.startCase(v)}
                  </option>
                ))}
              </select>
            </div>}
            {!_.isEqual(roles, ['customer']) && <div className="col">
              <select
                className="custom-select"
                value={filter.orgId}
                onChange={(e) => {
                  setSearchParams({ orgId: e.target.value });
                }}
              >
                <option value="">All OrgIds</option>
                {showrms.map((a) => (
                  <option key={a.orgId} value={a.orgId}>
                    {`${a.orgId} - ${a.countryCode}`}
                  </option>
                ))}
              </select>
            </div>}
            <div className="col">
              <select
                className="custom-select"
                value={filter.location}
                onChange={(e) => {
                  setSearchParams({ location: e.target.value });
                }}
              >
                <option value="">All Location</option>
                {_.map(_.orderBy(advFilterData?.location, '_id'), ({ _id: v }) => (
                  <option value={v} key={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        emptyComponent={(
          <div className="text-center my-5">
            <p>No record found, please contact our sales for more information.</p>
          </div>
        )}
      >
        {() => (
          <>
            {!_.isEqual(roles, ['customer']) && <a className="btn btn-primary" href="/portal/shareStocks/new">
              Share Stocks
            </a>}
          </>
        )}
      </ListResource>
    </div>
  );
};

export default PageStocks;
